import { useEffect, useMemo } from "react";

/**
 * Lightweight SEO hook — updates title, meta description, canonical URL,
 * OpenGraph and Twitter tags on route change. Also injects an optional
 * JSON-LD structured-data block that is removed on unmount.
 */
export default function useSEO({
    title,
    description,
    canonical,
    ogImage,
    jsonLd,
} = {}) {
    // Stabilize jsonLd dep — callers commonly pass fresh object literals every render.
    // Serializing gives us a stable primitive to compare on.
    const jsonLdSerialized = useMemo(
        () => (jsonLd ? JSON.stringify(jsonLd) : ""),
        [jsonLd]
    );

    useEffect(() => {
        if (title) document.title = title;

        const upsertMeta = (attr, name, content) => {
            if (!content) return;
            let el = document.head.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };

        upsertMeta("name", "description", description);
        upsertMeta("property", "og:title", title);
        upsertMeta("property", "og:description", description);
        upsertMeta("property", "og:type", "website");
        upsertMeta("property", "og:url", canonical);
        upsertMeta("property", "og:image", ogImage);
        upsertMeta("name", "twitter:card", "summary_large_image");
        upsertMeta("name", "twitter:title", title);
        upsertMeta("name", "twitter:description", description);
        upsertMeta("name", "twitter:image", ogImage);

        if (canonical) {
            let link = document.head.querySelector('link[rel="canonical"]');
            if (!link) {
                link = document.createElement("link");
                link.setAttribute("rel", "canonical");
                document.head.appendChild(link);
            }
            link.setAttribute("href", canonical);
        }

        let scriptEl = null;
        if (jsonLdSerialized) {
            scriptEl = document.createElement("script");
            scriptEl.type = "application/ld+json";
            scriptEl.setAttribute("data-dynamic-jsonld", "true");
            scriptEl.textContent = jsonLdSerialized;
            document.head.appendChild(scriptEl);
        }

        return () => {
            if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
        };
    }, [title, description, canonical, ogImage, jsonLdSerialized]);
}
